import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FlaskConical, AlertTriangle, CheckCircle, XCircle,
  ChevronRight, Play, X, Calendar, TrendingUp
} from 'lucide-react';
import { api } from '../api/client';

const SYMBOLS     = ['XAUUSD', 'EURUSD', 'USDMXN'];
const TIMEFRAMES  = ['M1', 'M5', 'M15', 'H1', 'H4'];

const VERDICT_STYLE = {
  PASS:    { color: 'text-green-400',  bg: 'bg-green-400/10',  border: 'border-green-400/20',  icon: CheckCircle  },
  FAIL:    { color: 'text-red-400',    bg: 'bg-red-400/10',    border: 'border-red-400/20',    icon: XCircle      },
  REVIEW:  { color: 'text-amber-400',  bg: 'bg-amber-400/10',  border: 'border-amber-400/20',  icon: AlertTriangle},
  INVALID: { color: 'text-slate-500',  bg: 'bg-slate-500/10',  border: 'border-slate-500/20',  icon: XCircle      },
  PENDING: { color: 'text-slate-500',  bg: 'bg-slate-500/10',  border: 'border-slate-500/20',  icon: AlertTriangle},
};

function VerdictBadge({ verdict }) {
  const s = VERDICT_STYLE[verdict] || VERDICT_STYLE.PENDING;
  const Icon = s.icon;
  return (
    <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded border text-[10px] font-mono ${s.color} ${s.bg} ${s.border}`}>
      <Icon size={10} />
      {verdict}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Mexico_City'
  });
}

// ─── Modal nuevo backtest ──────────────────────────────────────────────────────
function NewBacktestModal({ strategies, onClose, onLaunched }) {
  const [form, setForm] = useState({
    symbol:       'XAUUSD',
    timeframe:    'M5',
    strategyName: strategies[0]?.name || '',
    from:         '',
    to:           '',
  });
  const [running, setRunning] = useState(false);
  const [error,   setError]   = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleRun = async () => {
    if (!form.from || !form.to) { setError('Fechas requeridas'); return; }
    setRunning(true);
    setError(null);
    try {
      await api.runBacktest({
        symbol:       form.symbol,
        timeframe:    form.timeframe,
        strategyName: form.strategyName,
        from:         new Date(form.from).toISOString(),
        to:           new Date(form.to).toISOString(),
      });
      onLaunched();
      onClose();
    } catch (e) {
      const msg = e.response?.data?.error || 'Error al ejecutar backtest';
      setError(msg);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-[#13131A] border border-[#2A2A3E] rounded-lg w-full max-w-md p-6 space-y-5">

        <div className="flex items-center justify-between">
          <h2 className="font-mono font-bold text-slate-100 text-sm flex items-center gap-2">
            <FlaskConical size={14} className="text-amber-400" />
            Nuevo Backtest
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300">
            <X size={16} />
          </button>
        </div>

        {/* Símbolo */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">Símbolo</label>
          <div className="flex gap-2">
            {SYMBOLS.map(s => (
              <button key={s} onClick={() => set('symbol', s)}
                className={`px-3 py-1.5 rounded text-[11px] font-mono border transition-colors ${
                  form.symbol === s
                    ? 'border-amber-400 bg-amber-400/10 text-amber-400'
                    : 'border-[#2A2A3E] text-slate-500 hover:border-slate-500'
                }`}>{s}</button>
            ))}
          </div>
        </div>

        {/* Timeframe */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">Timeframe</label>
          <div className="flex gap-2">
            {TIMEFRAMES.map(t => (
              <button key={t} onClick={() => set('timeframe', t)}
                className={`px-3 py-1.5 rounded text-[11px] font-mono border transition-colors ${
                  form.timeframe === t
                    ? 'border-amber-400 bg-amber-400/10 text-amber-400'
                    : 'border-[#2A2A3E] text-slate-500 hover:border-slate-500'
                }`}>{t}</button>
            ))}
          </div>
        </div>

        {/* Estrategia */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">Estrategia</label>
          <select
            value={form.strategyName}
            onChange={e => set('strategyName', e.target.value)}
            className="w-full px-3 py-2 rounded text-[11px] font-mono border border-[#2A2A3E] bg-[#0A0A0F] text-slate-300 focus:border-amber-400 focus:outline-none"
          >
            {strategies.map(s => (
              <option key={s.id} value={s.name}>{s.name}</option>
            ))}
          </select>
        </div>

        {/* Fechas */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">Desde</label>
            <input type="date" value={form.from} onChange={e => set('from', e.target.value)}
              className="w-full px-3 py-2 rounded text-[11px] font-mono border border-[#2A2A3E] bg-[#0A0A0F] text-slate-300 focus:border-amber-400 focus:outline-none" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">Hasta</label>
            <input type="date" value={form.to} onChange={e => set('to', e.target.value)}
              className="w-full px-3 py-2 rounded text-[11px] font-mono border border-[#2A2A3E] bg-[#0A0A0F] text-slate-300 focus:border-amber-400 focus:outline-none" />
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-400 text-[11px] font-mono border border-red-400/20 bg-red-400/5 px-3 py-2 rounded">
            <AlertTriangle size={11} /> {error}
          </div>
        )}

        {running && (
          <div className="flex items-center gap-2 text-amber-400 text-[11px] font-mono border border-amber-400/20 bg-amber-400/5 px-3 py-2 rounded">
            <div className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
            Ejecutando backtest... puede tardar hasta 60 segundos
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} disabled={running}
            className="flex-1 py-2 rounded text-[11px] font-mono border border-[#2A2A3E] text-slate-500 hover:text-slate-300 transition-colors disabled:opacity-50">
            Cancelar
          </button>
          <button onClick={handleRun} disabled={running}
            className="flex-1 py-2 rounded text-[11px] font-mono bg-amber-500 hover:bg-amber-400 text-black font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            <Play size={12} /> {running ? 'Ejecutando...' : 'Ejecutar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Página principal ──────────────────────────────────────────────────────────
export default function GymBacktests() {
  const navigate = useNavigate();
  const [results,    setResults]    = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [showModal,  setShowModal]  = useState(false);
  const [filterSym,  setFilterSym]  = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      api.getBacktestResults(),
      api.getStrategies(),
    ]).then(([r, s]) => {
      setResults(r.data);
      setStrategies(s.data);
    }).catch(() => setError('Error cargando backtests'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = filterSym
    ? results.filter(r => r.symbol === filterSym)
    : results;

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="flex items-center gap-3 text-amber-500 font-mono text-sm">
        <div className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
        Cargando backtests...
      </div>
    </div>
  );

  return (
    <div className="p-8 space-y-6">

      {showModal && (
        <NewBacktestModal
          strategies={strategies}
          onClose={() => setShowModal(false)}
          onLaunched={load}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-100 tracking-tight">Backtests</h1>
          <p className="text-xs text-slate-500 font-mono mt-1">{filtered.length} resultados</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded bg-amber-500 hover:bg-amber-400 text-black text-[11px] font-mono font-bold transition-colors"
        >
          <Play size={12} /> Nuevo backtest
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-400 text-xs font-mono border border-red-400/20 bg-red-400/5 px-3 py-2 rounded">
          <AlertTriangle size={12} /> {error}
        </div>
      )}

      {/* Filtro símbolo */}
      <div className="flex gap-2">
        {['', ...SYMBOLS].map(s => (
          <button key={s} onClick={() => setFilterSym(s)}
            className={`px-3 py-1.5 rounded text-[11px] font-mono border transition-colors ${
              filterSym === s
                ? 'border-amber-400 bg-amber-400/10 text-amber-400'
                : 'border-[#2A2A3E] text-slate-500 hover:border-slate-500'
            }`}>
            {s || 'Todos'}
          </button>
        ))}
      </div>

      {/* Tabla */}
      <div className="bg-[#13131A] border border-[#1E1E2E] rounded-lg overflow-hidden">
        <div className="grid grid-cols-[80px_80px_1fr_100px_80px_80px_40px] gap-0 px-4 py-2 border-b border-[#1E1E2E] text-[10px] font-mono text-slate-600 uppercase tracking-wider">
          <span>Símbolo</span>
          <span>TF</span>
          <span>Estrategia</span>
          <span>Período</span>
          <span>Win%</span>
          <span>Verdict</span>
          <span></span>
        </div>

        {filtered.length === 0 ? (
          <div className="px-4 py-8 text-center text-slate-600 font-mono text-xs">
            Sin resultados
          </div>
        ) : (
          <div className="divide-y divide-[#1E1E2E]">
            {filtered.map(r => (
              <div
                key={r.id}
                onClick={() => navigate(`/gym/backtests/${r.id}`)}
                className="grid grid-cols-[80px_80px_1fr_100px_80px_80px_40px] gap-0 px-4 py-3 items-center hover:bg-white/5 cursor-pointer transition-colors"
              >
                <span className="text-[11px] font-mono font-bold text-amber-400">{r.symbol}</span>
                <span className="text-[11px] font-mono text-slate-400">{r.timeframe}</span>
                <span className="text-[11px] font-mono text-slate-300 truncate pr-4">{r.strategy}</span>
                <span className="text-[10px] font-mono text-slate-600">
                  {formatDate(r.periodStart)} → {formatDate(r.periodEnd)}
                </span>
                <span className="text-[11px] font-mono text-slate-300">
                  {r.winRate ? `${r.winRate.toFixed(1)}%` : '—'}
                </span>
                <VerdictBadge verdict={r.verdict} />
                <ChevronRight size={14} className="text-slate-600 justify-self-end" />
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}